import * as THREE from "three";

export interface RoughStockResult {
  stock: THREE.Object3D;
  sizes: THREE.Vector3;
  center: THREE.Vector3;
  axes: [THREE.Vector3, THREE.Vector3, THREE.Vector3];
}

export interface RoughStockOptions {
  color?: THREE.ColorRepresentation;
  opacity?: number;
  alignToFace?: THREE.Vector3 | null;
}

// Bir Object3D'nin ALTINDAKI (kendisi dahil, iç içe gruplar dahil) TÜM mesh'lerin
// world-uzay vertex'lerini toplar. Eskiden yalnız object.mesh (tek, doğrudan mesh)
// okunuyordu — hedef çizim bir grup/iç içe yapıysa (tek .mesh'i olmayan FikoGrupCizim
// gibi) bu sessizce hiçbir vertex bulamıyor, min/max Infinity/-Infinity'de kalıyor ve
// (min+max)/2 = Infinity + (-Infinity) = NaN'e düşüyordu → yön seçilince kütük ölçüleri
// NaN oluyordu. Box3().setFromObject (varsayılan eksen-hizalı yol) zaten traverse ile
// çalıştığı için o hep doğruydu; yön seçimi/manuel eksen yolu ona eşitlendi.
function worldVertexleriTopla(object: THREE.Object3D): THREE.Vector3[] {
  const noktalar: THREE.Vector3[] = [];
  object.traverse((cocuk: any) => {
    if (!cocuk.isMesh || !cocuk.geometry) return;
    const geometry = cocuk.geometry as THREE.BufferGeometry;
    const positionAttribute = geometry.getAttribute('position');
    if (!positionAttribute) return;
    const positions = positionAttribute.array as ArrayLike<number>;
    for (let i = 0; i < positions.length; i += 3) {
      const v = new THREE.Vector3(positions[i], positions[i + 1], positions[i + 2]);
      v.applyMatrix4(cocuk.matrixWorld);
      noktalar.push(v);
    }
  });
  return noktalar;
}

export function createRoughStockAround(
  object: THREE.Object3D,
  options: RoughStockOptions = {}
): RoughStockResult | null {

  const color = options.color ?? 0x999999;
  const opacity = options.opacity ?? 0.5;

  // Basit AABB (Axis-Aligned Bounding Box) hesapla
  const box = new THREE.Box3().setFromObject(object);

  if (box.isEmpty()) return null;

  // Box merkezi ve boyutları
  const center = new THREE.Vector3();
  box.getCenter(center);

  const size = new THREE.Vector3();
  box.getSize(size);

  // Standart eksenler (X, Y, Z world axes)
  let X = new THREE.Vector3(1, 0, 0);
  let Y = new THREE.Vector3(0, 1, 0);
  let Z = new THREE.Vector3(0, 0, 1);
  let sizeX = size.x;
  let sizeY = size.y;
  let sizeZ = size.z;
  let newCenter = center.clone();

  // ---------------------
  //   Yüzeye göre hizala (Face Align)
  // ---------------------
  if (options.alignToFace) {
    const faceNormal = options.alignToFace.clone().normalize();

    // Z ekseni = face normal (yüzeye dik)
    Z = faceNormal.clone();

    // X ve Y eksenlerini face normal'a dik olacak şekilde oluştur
    // Yardımcı vektör (face normal'a paralel olmayan)
    let auxiliary = new THREE.Vector3(0, 1, 0);
    if (Math.abs(Z.dot(auxiliary)) > 0.9) {
      auxiliary = new THREE.Vector3(1, 0, 0);
    }

    // X = Z × auxiliary (yüzeye dik düzlemde)
    X = new THREE.Vector3().crossVectors(Z, auxiliary).normalize();
    // Y = Z × X (right-hand rule)
    Y = new THREE.Vector3().crossVectors(Z, X).normalize();

    // Geometrinin tüm vertices'ini yeni eksenlere projekte et
    // Min-max bul
    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;
    let minZ = Infinity, maxZ = -Infinity;

    // object.mesh sadece TEK dogrudan mesh'i kapsar; object bir grup/ic ice yapiysa
    // (tek .mesh'i olmayan FikoGrupCizim gibi) bu yol hic vertex bulamaz ve
    // min/max Infinity/-Infinity'de kalip (min+max)/2 = NaN uretirdi (yon secilince
    // kutuk olculeri NaN olma bug'i). worldVertexleriTopla tum alt mesh'leri traverse
    // ederek toplar - ustteki varsayilan AABB yolunun kullandigi yontemle ayni.
    const dunyaNoktalari = worldVertexleriTopla(object);

    for (const vertex of dunyaNoktalari) {
      const projX = vertex.dot(X);
      const projY = vertex.dot(Y);
      const projZ = vertex.dot(Z);

      minX = Math.min(minX, projX);
      maxX = Math.max(maxX, projX);
      minY = Math.min(minY, projY);
      maxY = Math.max(maxY, projY);
      minZ = Math.min(minZ, projZ);
      maxZ = Math.max(maxZ, projZ);
    }

    if (dunyaNoktalari.length === 0) {
      minX = 0; maxX = size.x;
      minY = 0; maxY = size.y;
      minZ = 0; maxZ = size.z;
    }

    sizeX = maxX - minX;
    sizeY = maxY - minY;
    sizeZ = maxZ - minZ;

    // Kutuk merkezi (yeni koordinat sisteminde)
    const newCoordsCenterX = (minX + maxX) / 2;
    const newCoordsCenterY = (minY + maxY) / 2;
    const newCoordsCenterZ = (minZ + maxZ) / 2;

    // World koordinatlarına çevir: merkez = centerX*X + centerY*Y + centerZ*Z
    newCenter = new THREE.Vector3(0, 0, 0);
    newCenter.addScaledVector(X, newCoordsCenterX);
    newCenter.addScaledVector(Y, newCoordsCenterY);
    newCenter.addScaledVector(Z, newCoordsCenterZ);
  }

  const axes: [THREE.Vector3, THREE.Vector3, THREE.Vector3] = [X, Y, Z];

  // Stock Group oluştur (mesh + edges)
  const stock = new THREE.Group();

  // Fill mesh
  const stockGeom = new THREE.BoxGeometry(sizeX, sizeY, sizeZ);
  const stockMat = new THREE.MeshStandardMaterial({
    color,
    transparent: true,
    opacity,
    depthWrite: false  // Arkasındaki highlight'ların görünmesi için
  });
  const fillMesh = new THREE.Mesh(stockGeom, stockMat);

  // Stock'u raycast edilemez yap (parent mesh'e tıklanabilmesi için)
  fillMesh.raycast = () => {};

  stock.add(fillMesh);

  // Edges (sadece kenarlar, çapraz çizgiler yok)
  const edgeGeom = new THREE.EdgesGeometry(stockGeom);
  const edgeMat = new THREE.LineBasicMaterial({ color: color, linewidth: 2 });
  const edges = new THREE.LineSegments(edgeGeom, edgeMat);

  // Edges de raycast edilemez
  edges.raycast = () => {};

  stock.add(edges);

  // Position ve rotation set et
  stock.position.copy(newCenter);
  stock.setRotationFromMatrix(new THREE.Matrix4().makeBasis(axes[0], axes[1], axes[2]));

  const returnSize = new THREE.Vector3(sizeX, sizeY, sizeZ);

  return { stock, sizes: returnSize, center: newCenter, axes };
}
